import { useCallback, useMemo, useState } from 'react'
import { ArrowDownLeft, Coins, Plus, Send, Trash2 } from 'lucide-react'
import { Link } from 'react-router'
import { AgGridReact } from 'ag-grid-react'
import type { ColDef, ICellRendererParams, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community'
import { ROUTE_PAYMENT } from '@klear/constants'
import { PAAS_STATUS_LABELS, type PaasStatus } from '@klear/paas/constants'
import { useConfirmDialog } from '@klear/ui/components/alert-dialog'
import { Button } from '@klear/ui/components/button'
import { useKlearAgGrid } from '@klear/ui/hooks/use-klear-ag-grid'
import {
  paasAllocationRows,
  usePaasDisbursables,
  usePaasMutations,
  type PaasAllocationRow,
  type PaasDisbursableRow,
  type PaasPositionRow
} from '@klear/ui/hooks/use-paas'
import FundPaasDialog from '@/components/FundPaasDialog'
import PaasFeeTermsDialog from '@/components/PaasFeeTermsDialog'
import ViewIcon from '@/icons/ViewIcon'
import { paymentIsDraft } from '@klear/payment/status'

export type PaasGridVariant = 'collections' | 'disbursements'

const PAAS_VIEW = {
  POSITIONS: 'positions',
  ALLOCATIONS: 'allocations',
  DISBURSABLE: 'disbursable'
} as const

type PaasView = (typeof PAAS_VIEW)[keyof typeof PAAS_VIEW]

const POSITION_VIEW_OPTIONS: { value: PaasView; label: string }[] = [
  { value: PAAS_VIEW.POSITIONS, label: 'Positions' },
  { value: PAAS_VIEW.ALLOCATIONS, label: 'Payments' }
]

const DISBURSABLE_VIEW_OPTION: { value: PaasView; label: string } = {
  value: PAAS_VIEW.DISBURSABLE,
  label: 'Draft payments'
}

const paasStatusLabel = (status: string): string => PAAS_STATUS_LABELS[status as PaasStatus] ?? status

const dash = (value: string | null | undefined): string => (value?.trim() ? value : '—')

const moneyCellText = (value: unknown): string => value?.toString() ?? '-'

function PaymentLink({ id, label }: { id: number; label: string }) {
  return (
    <Link
      to={`/${ROUTE_PAYMENT}/${id}`}
      className="text-foreground hover:text-foreground underline font-medium"
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      {label}
    </Link>
  )
}

export default function PaasPositionsGrid({
  positions,
  isLoading,
  variant
}: {
  positions: PaasPositionRow[]
  isLoading: boolean
  variant: PaasGridVariant
}) {
  const agGrid = useKlearAgGrid()
  const [view, setView] = useState<PaasView>(PAAS_VIEW.POSITIONS)
  const [fundTarget, setFundTarget] = useState<PaasDisbursableRow | null>(null)
  const [feeTarget, setFeeTarget] = useState<PaasPositionRow | null>(null)

  const isDisbursableView = view === PAAS_VIEW.DISBURSABLE
  const canOriginate = variant === 'disbursements'
  const { disbursables, isLoading: isDisbursablesLoading } = usePaasDisbursables({}, canOriginate)
  const { disburse, isDisbursing, deallocate, isDeallocating, collect, isCollecting } = usePaasMutations()
  const { confirm, ConfirmDialog } = useConfirmDialog()

  const allocationRows = useMemo(() => paasAllocationRows(positions), [positions])

  const onDisburse = useCallback(
    (row: PaasPositionRow) => {
      const drafted = row.advances.find((allocation) => paymentIsDraft(allocation.payment.status))
      if (drafted == null) return
      confirm({
        title: `Disburse ${row.idDisplay}?`,
        description:
          `This sends ${drafted.payment.amount.toString()} to ${row.traderOrganizationName ?? 'the trader'} now. ` +
          `The advance becomes collectable once it settles.`,
        onConfirm: async () => {
          await disburse({ paasPaymentId: drafted.payment.id })
        }
      })
    },
    [confirm, disburse]
  )

  const onCollect = useCallback(
    (row: PaasPositionRow) => {
      const fee = row.collectableFee
      const parts = [`${row.collectableAmount.toString()} of principal back to the funder`]
      if (fee?.isPositive()) parts.push(`${fee.toString()} of fees to Klear Inc`)
      confirm({
        title: `Collect ${row.idDisplay}?`,
        description:
          `This pulls ${parts.join(', and ')} from ${row.traderOrganizationName ?? 'the trader'} now, ` +
          `reversing the advance. To collect only part of it, use the PaaS MCP tools.`,
        onConfirm: async () => {
          await collect({ supplierPaymentId: row.supplierPayment.id })
        }
      })
    },
    [confirm, collect]
  )

  const PositionActionCell = useCallback(
    (props: ICellRendererParams<PaasPositionRow>) => {
      const row = props.data
      if (!row) return null
      return (
        <div className="flex items-center gap-2">
          <Link
            to={`/${ROUTE_PAYMENT}/${row.supplierPayment.id}`}
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <ViewIcon />
            View
          </Link>
          {row.advances.some((allocation) => paymentIsDraft(allocation.payment.status)) && (
            <button
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground disabled:opacity-50"
              disabled={isDisbursing}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation()
                onDisburse(row)
              }}
            >
              <Send className="h-3.5 w-3.5" />
              Disburse
            </button>
          )}
          {row.hasCollectableBalance && (
            <button
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground disabled:opacity-50"
              disabled={isCollecting}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation()
                onCollect(row)
              }}
            >
              <ArrowDownLeft className="h-3.5 w-3.5" />
              Collect
            </button>
          )}
          <button
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation()
              setFeeTarget(row)
            }}
          >
            <Coins className="h-3.5 w-3.5" />
            Fee
          </button>
        </div>
      )
    },
    [onDisburse, isDisbursing, onCollect, isCollecting, setFeeTarget]
  )

  const AllocationActionCell = useCallback(
    (props: ICellRendererParams<PaasAllocationRow>) => {
      const row = props.data
      if (!row || !paymentIsDraft(row.status)) return null
      return (
        <button
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-destructive disabled:opacity-50"
          disabled={isDeallocating}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation()
            confirm({
              title: `Remove allocation ${row.allocationId}?`,
              description:
                `This releases ${row.allocated.toString()} of payment #${row.paymentId} from ${row.paasIdDisplay}. ` +
                `The payment keeps its amount and becomes available to allocate elsewhere.`,
              onConfirm: async () => {
                await deallocate({ allocationId: row.allocationId })
              }
            })
          }}
        >
          <Trash2 className="h-3.5 w-3.5" />
          Remove
        </button>
      )
    },
    [confirm, deallocate, isDeallocating]
  )

  const FundCell = useCallback((props: ICellRendererParams<PaasDisbursableRow>) => {
    const row = props.data
    if (!row) return null
    return (
      <button
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation()
          setFundTarget(row)
        }}
      >
        <Plus className="h-3.5 w-3.5" />
        Mark as PaaS
      </button>
    )
  }, [])

  const positionColumnDefs = useMemo<ColDef<PaasPositionRow>[]>(() => {
    const identity: ColDef<PaasPositionRow>[] = [
      {
        headerName: 'Payable',
        colId: 'idDisplay',
        field: 'idDisplay',
        width: 120,
        minWidth: 110,
        cellRenderer: (props: ICellRendererParams<PaasPositionRow>) =>
          props.data ? <PaymentLink id={props.data.supplierPayment.id} label={props.data.idDisplay} /> : '—'
      },
      {
        headerName: 'Status',
        colId: 'status',
        valueGetter: (params: ValueGetterParams<PaasPositionRow>) =>
          params.data ? paasStatusLabel(params.data.status) : '',
        width: 170,
        minWidth: 150,
        cellClassRules: {
          'text-destructive font-medium': (params) => params.data?.needsReconciliation === true
        }
      },
      {
        headerName: 'Trader',
        colId: 'traderOrganizationName',
        field: 'traderOrganizationName',
        valueFormatter: (params: ValueFormatterParams<PaasPositionRow, string | null>) => dash(params.value)
      },
      {
        headerName: 'Supplier',
        colId: 'supplierOrganizationName',
        field: 'supplierOrganizationName',
        valueFormatter: (params: ValueFormatterParams<PaasPositionRow, string>) => dash(params.value)
      }
    ]

    const financing: ColDef<PaasPositionRow>[] = [
      { headerName: 'Payable amount', colId: 'payableAmount', field: 'payableAmount', type: ['money', 'rightAligned'] },
      { headerName: 'Financed', colId: 'financedAmount', field: 'financedAmount', type: ['money', 'rightAligned'] },
      {
        headerName: 'Committed',
        colId: 'committedAmount',
        field: 'committedAmount',
        type: ['money', 'rightAligned']
      },
      { headerName: 'Payable due', colId: 'payableDate', field: 'payableDate', type: 'klearDate' }
    ]

    const collection: ColDef<PaasPositionRow>[] = [
      { headerName: 'Repaid', colId: 'repaidAmount', field: 'repaidAmount', type: ['money', 'rightAligned'] },
      {
        headerName: 'Outstanding',
        colId: 'outstandingAmount',
        field: 'outstandingAmount',
        type: ['money', 'rightAligned']
      },
      { headerName: 'Fees in', colId: 'feesCollected', field: 'feesCollected', type: ['money', 'rightAligned'] },
      {
        headerName: 'Fees due',
        colId: 'feesRemaining',
        field: 'feesRemaining',
        type: ['money', 'rightAligned'],
        valueFormatter: (params: ValueFormatterParams<PaasPositionRow>) =>
          params.data?.hasFeeTerms === false ? 'No fee terms' : moneyCellText(params.value)
      },
      { headerName: 'Total due', colId: 'totalOutstanding', field: 'totalOutstanding', type: ['money', 'rightAligned'] }
    ]

    const trailing: ColDef<PaasPositionRow>[] = [
      {
        headerName: 'Over-repaid',
        colId: 'overRepaidAmount',
        field: 'overRepaidAmount',
        type: ['money', 'rightAligned'],
        cellClassRules: {
          'text-destructive font-medium': (params) => params.data?.overRepaidAmount?.isPositive() === true
        }
      },
      {
        headerName: 'Returned',
        colId: 'reversedAmount',
        field: 'reversedAmount',
        type: ['money', 'rightAligned'],
        cellClassRules: {
          'text-destructive font-medium': (params) => params.data?.reversedAmount?.isPositive() === true
        }
      },
      {
        headerName: 'Payments',
        colId: 'allocationCount',
        valueGetter: (params: ValueGetterParams<PaasPositionRow>) => params.data?.allocations.length ?? 0,
        filter: 'agNumberColumnFilter',
        type: 'rightAligned',
        width: 110,
        minWidth: 100
      },
      { headerName: 'Repaid on', colId: 'repaidDate', field: 'repaidDate', type: 'klearDate' },
      {
        headerName: '',
        colId: 'actions',
        type: 'actions',
        pinned: 'right',
        cellStyle: { overflow: 'visible' },
        width: 260,
        minWidth: 260,
        maxWidth: 260,
        cellRenderer: PositionActionCell
      }
    ]

    return variant === 'disbursements'
      ? [...identity, ...financing, ...collection, ...trailing]
      : [...identity, ...collection, ...financing, ...trailing]
  }, [variant, PositionActionCell])

  const allocationColumnDefs = useMemo<ColDef<PaasAllocationRow>[]>(
    () => [
      {
        headerName: 'Payable',
        colId: 'paasIdDisplay',
        field: 'paasIdDisplay',
        width: 120,
        minWidth: 110,
        cellRenderer: (props: ICellRendererParams<PaasAllocationRow>) =>
          props.data ? <PaymentLink id={props.data.supplierPaymentId} label={props.data.paasIdDisplay} /> : '—'
      },
      {
        headerName: 'Trader',
        colId: 'traderOrganizationName',
        field: 'traderOrganizationName',
        valueFormatter: (params: ValueFormatterParams<PaasAllocationRow, string | null>) => dash(params.value)
      },
      {
        headerName: 'Payment',
        colId: 'paymentId',
        field: 'paymentId',
        width: 130,
        minWidth: 120,
        cellRenderer: (props: ICellRendererParams<PaasAllocationRow>) =>
          props.data ? <PaymentLink id={props.data.paymentId} label={`#${props.data.paymentId}`} /> : '—'
      },
      { headerName: 'Type', colId: 'paymentTypeLabel', field: 'paymentTypeLabel' },
      {
        headerName: 'Alloc.',
        colId: 'allocationId',
        field: 'allocationId',
        filter: 'agNumberColumnFilter',
        width: 100,
        minWidth: 90
      },
      {
        headerName: 'Allocated',
        colId: 'allocated',
        field: 'allocated',
        type: ['money', 'rightAligned']
      },
      {
        headerName: 'Payment total',
        colId: 'paymentAmount',
        field: 'paymentAmount',
        type: ['money', 'rightAligned']
      },
      { headerName: 'Status', colId: 'status', field: 'status' },
      { headerName: 'Effective date', colId: 'effectiveDate', field: 'effectiveDate', type: 'klearDate' },
      {
        headerName: '',
        colId: 'actions',
        type: 'actions',
        pinned: 'right',
        cellStyle: { overflow: 'visible' },
        width: 120,
        minWidth: 120,
        maxWidth: 120,
        cellRenderer: AllocationActionCell
      }
    ],
    [AllocationActionCell]
  )

  const disbursableColumnDefs = useMemo<ColDef<PaasDisbursableRow>[]>(
    () => [
      {
        headerName: 'Payable',
        colId: 'supplierPaymentIdDisplay',
        field: 'supplierPaymentIdDisplay',
        width: 130,
        minWidth: 120,
        cellRenderer: (props: ICellRendererParams<PaasDisbursableRow>) =>
          props.data ? (
            <PaymentLink id={props.data.supplierPaymentId} label={props.data.supplierPaymentIdDisplay} />
          ) : (
            '—'
          )
      },
      { headerName: 'Trader', colId: 'traderOrganizationName', field: 'traderOrganizationName' },
      {
        headerName: 'Supplier',
        colId: 'supplierOrganizationName',
        field: 'supplierOrganizationName',
        valueFormatter: (params: ValueFormatterParams<PaasDisbursableRow, string | null>) => dash(params.value)
      },
      { headerName: 'Payable amount', colId: 'payableAmount', field: 'payableAmount', type: ['money', 'rightAligned'] },
      { headerName: 'Due', colId: 'effectiveDate', field: 'effectiveDate', type: 'klearDate' },
      {
        headerName: '',
        colId: 'actions',
        type: 'actions',
        pinned: 'right',
        cellStyle: { overflow: 'visible' },
        width: 140,
        minWidth: 140,
        maxWidth: 140,
        cellRenderer: FundCell
      }
    ],
    [FundCell]
  )

  const defaultColDef: ColDef = {
    ...agGrid.defaultColDef,
    flex: 1,
    minWidth: 120
  }

  const gridProps = {
    rowHeight: 35,
    headerHeight: 40,
    defaultColDef,
    columnTypes: agGrid.columnTypes,
    dataTypeDefinitions: agGrid.dataTypeDefinitions,
    animateRows: true,
    enableCellTextSelection: false,
    pagination: true,
    paginationPageSize: 20,
    theme: agGrid.theme
  }

  const empty = (message: string) => (
    <div className="flex items-center justify-center h-full text-muted-foreground">{message}</div>
  )

  return (
    <div className="flex-1 min-h-0 flex flex-col gap-3">
      <div className="flex items-center gap-1">
        {(canOriginate ? [...POSITION_VIEW_OPTIONS, DISBURSABLE_VIEW_OPTION] : POSITION_VIEW_OPTIONS).map((opt) => (
          <Button
            key={opt.value}
            variant={view === opt.value ? 'default' : 'outline'}
            size="sm"
            onClick={() => setView(opt.value)}
          >
            {opt.label}
            {opt.value === PAAS_VIEW.ALLOCATIONS && allocationRows.length > 0 ? ` (${allocationRows.length})` : ''}
            {opt.value === PAAS_VIEW.DISBURSABLE && disbursables.length > 0 ? ` (${disbursables.length})` : ''}
          </Button>
        ))}
      </div>
      <div className="w-full h-full min-h-[600px]">
        {isDisbursableView ? (
          isDisbursablesLoading ? (
            empty('Loading...')
          ) : disbursables.length === 0 ? (
            empty('No draft supplier payments to mark as PaaS.')
          ) : (
            <AgGridReact<PaasDisbursableRow>
              key="paas-disbursables-grid"
              rowData={disbursables}
              columnDefs={disbursableColumnDefs}
              {...gridProps}
            />
          )
        ) : isLoading ? (
          empty('Loading...')
        ) : view === PAAS_VIEW.ALLOCATIONS ? (
          allocationRows.length === 0 ? (
            empty('No payments allocated to a PaaS position.')
          ) : (
            <AgGridReact<PaasAllocationRow>
              key="paas-allocations-grid"
              rowData={allocationRows}
              columnDefs={allocationColumnDefs}
              {...gridProps}
            />
          )
        ) : positions.length === 0 ? (
          empty('No PaaS positions.')
        ) : (
          <AgGridReact<PaasPositionRow>
            key={`paas-positions-grid-${variant}`}
            rowData={positions}
            columnDefs={positionColumnDefs}
            {...gridProps}
          />
        )}
      </div>
      {ConfirmDialog}
      {fundTarget != null && (
        <FundPaasDialog
          disbursable={fundTarget}
          onOpenChange={(next) => {
            if (!next) setFundTarget(null)
          }}
        />
      )}
      {feeTarget != null && (
        <PaasFeeTermsDialog
          position={feeTarget}
          onOpenChange={(next) => {
            if (!next) setFeeTarget(null)
          }}
        />
      )}
    </div>
  )
}
